import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'
import AdminLayout from '../../components/Admin/AdminLayout'
import { SettingsAvatar } from '../../components/Settings/SettingsPortalUi'
import { useAuth } from '../../contexts/AuthContext'

export default function AdminProfile() {
  const { user, refreshUser } = useAuth()
  const [avatarUploading, setAvatarUploading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' })
  const [profile, setProfile] = useState({
    realName: user?.real_name || '',
    position: user?.position || '',
    phone: user?.phone || '',
  })

  React.useEffect(() => {
    setProfile({
      realName: user?.real_name || '',
      position: user?.position || '',
      phone: user?.phone || '',
    })
  }, [user?.id, user?.real_name, user?.position, user?.phone])

  const handleAvatarUpload = async (file) => {
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Please upload a JPG or PNG image')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be 5MB or smaller')
      return
    }
    setAvatarUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      await axios.post('/api/users/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      await refreshUser?.()
      toast.success('Photo updated')
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to upload photo')
    } finally {
      setAvatarUploading(false)
    }
  }

  const handleAvatarRemove = async () => {
    setAvatarUploading(true)
    try {
      await axios.delete('/api/users/avatar')
      await refreshUser?.()
      toast.success('Photo removed')
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove photo')
    } finally {
      setAvatarUploading(false)
    }
  }

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      await axios.put('/api/users/update-profile', profile)
      await refreshUser?.()
      toast.success('Profile updated')
    } catch {
      toast.error('Failed to update profile')
    } finally {
      setSaving(false)
    }
  }

  const handleChangePassword = async (e) => {
    e.preventDefault()
    if (passwords.new !== passwords.confirm) {
      toast.error('Passwords do not match')
      return
    }
    if (passwords.new.length < 8) {
      toast.error('New password must be at least 8 characters')
      return
    }
    setSaving(true)
    try {
      await axios.post('/api/users/change-password', {
        currentPassword: passwords.current,
        newPassword: passwords.new,
      })
      toast.success('Password changed successfully')
      setPasswords({ current: '', new: '', confirm: '' })
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change password')
    } finally {
      setSaving(false)
    }
  }

  return (
    <AdminLayout title="My Profile" subtitle="Your admin account profile and avatar">
      <div className="admin-settings-alert info">
        <span>Your avatar appears in the admin top bar and in chat across the platform.</span>
      </div>
      <div className="admin-card">
        <div className="admin-card-header">
          <span className="admin-card-title">Profile photo</span>
        </div>
        <div className="admin-card-body">
          <div className="admin-profile-avatar-wrap">
            <SettingsAvatar
              name={profile.realName || user?.real_name || user?.system_name}
              avatarUrl={user?.avatar_url}
              onUpload={handleAvatarUpload}
              onRemove={handleAvatarRemove}
              uploading={avatarUploading}
            />
          </div>
        </div>
      </div>
      <div className="admin-card">
        <div className="admin-card-header">
          <span className="admin-card-title">Basic information</span>
        </div>
        <div className="admin-card-body admin-settings-grid-2">
          <div className="admin-field-group">
            <label className="admin-field-label">Full name</label>
            <input
              className="admin-field-input"
              value={profile.realName}
              onChange={(e) => setProfile((p) => ({ ...p, realName: e.target.value }))}
            />
          </div>
          <div className="admin-field-group">
            <label className="admin-field-label">Job title</label>
            <input
              className="admin-field-input"
              value={profile.position}
              onChange={(e) => setProfile((p) => ({ ...p, position: e.target.value }))}
            />
          </div>
          <div className="admin-field-group">
            <label className="admin-field-label">Work email</label>
            <input className="admin-field-input" value={user?.work_email || ''} disabled />
          </div>
          <div className="admin-field-group">
            <label className="admin-field-label">Phone</label>
            <input
              className="admin-field-input"
              value={profile.phone}
              onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))}
            />
          </div>
        </div>
      </div>
      <div className="admin-settings-btn-group">
        <button type="button" className="admin-btn primary" disabled={saving} onClick={handleSave}>
          {saving ? 'Saving…' : 'Save profile'}
        </button>
      </div>
      <div className="admin-card">
        <div className="admin-card-header">
          <span className="admin-card-title">Login password</span>
        </div>
        <div className="admin-card-body">
          <form onSubmit={handleChangePassword} style={{ maxWidth: 480 }}>
            <div className="admin-field-group">
              <label className="admin-field-label">Current password</label>
              <input
                type="password"
                className="admin-field-input"
                value={passwords.current}
                onChange={(e) => setPasswords((p) => ({ ...p, current: e.target.value }))}
                placeholder="Enter current password"
                autoComplete="current-password"
                required
              />
            </div>
            <div className="admin-field-group">
              <label className="admin-field-label">New password</label>
              <input
                type="password"
                className="admin-field-input"
                value={passwords.new}
                onChange={(e) => setPasswords((p) => ({ ...p, new: e.target.value }))}
                placeholder="Min 8 characters"
                minLength={8}
                autoComplete="new-password"
                required
              />
            </div>
            <div className="admin-field-group">
              <label className="admin-field-label">Confirm new password</label>
              <input
                type="password"
                className="admin-field-input"
                value={passwords.confirm}
                onChange={(e) => setPasswords((p) => ({ ...p, confirm: e.target.value }))}
                placeholder="Repeat new password"
                autoComplete="new-password"
                required
              />
            </div>
            <p className="admin-field-hint" style={{ marginTop: 0 }}>
              Forgot your password?{' '}
              <Link to="/forgot-password" style={{ color: 'var(--admin-primary, #0f3d8a)' }}>
                Reset via email
              </Link>
            </p>
            <div className="admin-settings-btn-group">
              <button type="submit" className="admin-btn primary" disabled={saving}>
                {saving ? 'Changing…' : 'Change password'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  )
}
